import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterComponent from "../TabFilter";
import UsersTable from "./UsersTable";
import UserDetailsTabs from "./UserDetailsTabs";
import { societyUserList } from "../../../../utils/api";
import { CsvDownApi } from "../../../../v2/integrations/export";
import config from "../../../../utils/config";
import { Button, PageLoader } from "../../../../v2/design-system";
import { theme } from "../../../../v2/design-system/theme";
import styled from "styled-components";
import { HubFlexRow, HubPageShell, HubTableOverlay, HubTitleXl } from "../societyHubShell";
import { v2PaginationZeroBased } from "../tableShared";

const FilterWrap = styled.div`
  width: 20%;
  max-width: 100%;
  margin-top: ${theme.spacing.md};
`;

const TableWrap = styled.div`
  position: relative;
  margin-top: ${theme.spacing.md};
`;

const filterForm = [
  {
    name: "mobileFilter",
    type: "text",
    placeholder: "Search by Mobile",
    value: "",
    maxLength: "10",
  },
];

const Users = ({ userid }) => {
  const [showTable, setShowTable] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const location = useLocation();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filters, setFilters] = useState({ mobileFilter: "" });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (filters.mobileFilter.length >= 3 || filters.mobileFilter.length === 0) {
        setDebouncedFilters(filters);
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [filters]);

  useEffect(() => {
    if (location.state?.user) {
      setSelectedUser(location.state.user);
      setShowTable(false);
    }
  }, [location.state]);

  const handleUserView = (user) => {
    setSelectedUser(user);
    setShowTable(false);
  };

  const handleBack = () => {
    setShowTable(true);
    setSelectedUser(null);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const payload = {
          mobile: debouncedFilters.mobileFilter,
          societyApiUuidId: userid,
          pageNo: page,
          pageSize: itemsPerPage,
        };
        const res = await societyUserList(payload);
        const data = res?.data?.data;
        setUsers(data?.data || []);
        setTotalRecords(data?.totalRecords || 0);
      } catch {
        setUsers([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page, itemsPerPage, debouncedFilters, userid]);

  const csvHeaders = [
    { key: "createdDate", label: "Created Date" },
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "mobileNo", label: "Mobile No." },
    { key: "role", label: "Role" },
    { key: "status", label: "Kyc Status" },
  ];

  const csvParams = {
    mobile: debouncedFilters.mobileFilter,
    societyApiUuidId: userid,
    pageNo: page,
    pageSize: itemsPerPage,
  };

  return (
    <HubPageShell>
      <HubFlexRow $mb={theme.spacing.md}>
        {!showTable && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            leftIcon={<ArrowBackIcon sx={{ fontSize: 20 }} />}
            onClick={handleBack}
          >
            Back to Users List
          </Button>
        )}
      </HubFlexRow>

      {showTable && (
        <HubFlexRow $justify="space-between" $mb={theme.spacing.md}>
          <HubTitleXl>Users List</HubTitleXl>
          {users.length > 0 && (
            <CsvDownApi
              headers={csvHeaders}
              url={`${config.apis.societyPartnerHub.ALL_USERS}`}
              params={csvParams}
              method="post"
              reportName="UsersList.csv"
              title="Download CSV"
            />
          )}
        </HubFlexRow>
      )}

      {showTable ? (
        <>
          <FilterWrap>
            <FilterComponent
              filterFields={[{ ...filterForm[0], value: filters.mobileFilter }]}
              onFilterChange={(val, name) => setFilters({ ...filters, [name]: val })}
              showButton={false}
            />
          </FilterWrap>

          <TableWrap>
            <UsersTable
              data={users}
              pagination={v2PaginationZeroBased({
                pageZeroBased: page,
                pageSize: itemsPerPage,
                total: totalRecords,
                setPageZeroBased: setPage,
                setPageSize: setItemsPerPage,
              })}
              onUserView={handleUserView}
            />
            {loading && (
              <HubTableOverlay>
                <PageLoader />
              </HubTableOverlay>
            )}
          </TableWrap>
        </>
      ) : (
        <UserDetailsTabs user={selectedUser} />
      )}
    </HubPageShell>
  );
};

export default Users;
